"use client";

import { Link } from "@/i18n/navigation";
import React from "react";
import { useTranslations } from "next-intl";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";

interface Faq {
	question: string;
	answer: React.ReactNode;
}

function FaqSectionHeader() {
	const t = useTranslations("faq");
	return (
		<div className="flex flex-col gap-4 border-t border-r px-8 py-16 text-left md:px-12">
			<h2
				className="text-left font-medium text-3xl leading-tight tracking-[-2px] md:text-4xl"
				id="faq-title"
			>
				{t("title")}
			</h2>
		</div>
	);
}

const FaqList = React.memo(function FaqList({ faqs }: { faqs: Faq[] }) {
	return (
		<div className="flex flex-col gap-0">
			<Accordion className="w-full" collapsible type="single">
				{faqs.map((faq, i) => (
					<AccordionItem
						className="w-full border-t border-b-0 py-2"
						key={i}
						value={String(i)}
					>
						<AccordionTrigger className="px-4 text-left font-medium hover:no-underline md:px-6">
							{faq.question}
						</AccordionTrigger>
						<AccordionContent className="px-4 text-muted-foreground md:px-6">
							{faq.answer}
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</div>
	);
});

export default function FAQSection() {
	const t = useTranslations("faq");

	const faqs: Faq[] = [
		{ question: t("q1"), answer: t("a1") },
		{ question: t("q2"), answer: t("a2") },
		{ question: t("q3"), answer: t("a3") },
		{ question: t("q4"), answer: t("a4") },
		{ question: t("q5"), answer: t("a5") },
		{ question: t("q6"), answer: t("a6") },
		{ question: t("q7"), answer: t("a7") },
		{ question: t("q8"), answer: t("a8") },
		{
			question: t("q9"),
			answer: t.rich("a9", {
				link: (chunks) => (
					<Link
						className="underline"
						href="https://insigh.to/b/projme"
						rel="noopener noreferrer"
						target="_blank"
					>
						{chunks}
					</Link>
				),
			}),
		},
		{ question: t("q10"), answer: t("a10") },
		{ question: t("q11"), answer: t("a11") },
		{ question: t("q12"), answer: t("a12") },
		{ question: t("q13"), answer: t("a13") },
		{ question: t("q14"), answer: t("a14") },
		{
			question: t("q15"),
			answer: t.rich("a15", {
				link: (chunks) => (
					<Link
						className="underline"
						href="https://github.com/preetsuthar17/ikiform"
						rel="noopener noreferrer"
						target="_blank"
					>
						{chunks}
					</Link>
				),
			}),
		},
	];

	const [firstHalf, secondHalf] = React.useMemo(() => {
		const midpoint = Math.ceil(faqs.length / 2);
		return [faqs.slice(0, midpoint), faqs.slice(midpoint)];
	}, [faqs]);

	return (
		<section aria-labelledby="faq-title" className="mx-auto w-full max-w-7xl">
			<div className="mx-auto flex w-full flex-col">
				<div className="mx-auto flex w-full border border-t-0 max-md:flex-col">
					<FaqSectionHeader />
					<div className="grid w-full grid-cols-1">
						<FaqList faqs={firstHalf} />
						<FaqList faqs={secondHalf} />
					</div>
				</div>
			</div>
		</section>
	);
}
